import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { MenubarModule } from 'primeng/menubar';
import { ToolbarModule } from 'primeng/toolbar';
import { filter, Observable, startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../../../../core/auth/auth.service';
import { HeaderMenuLoggedComponent } from '../header-menu-logged/header-menu-logged.component';
import { HeaderTextIconComponent } from '../header-text-icon/header-text-icon.component';

@Component({
  selector: 'app-header-web',
  standalone: true,
  imports: [CommonModule, HeaderMenuLoggedComponent, MenubarModule, ToolbarModule, HeaderTextIconComponent],
  templateUrl: './header-web.component.html',
  styleUrls: ['./header-web.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderWebComponent implements OnInit {
  items$!: Observable<MenuItem[]>;
  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  readonly isGestionActive = computed(() => this.currentUrl().startsWith('/admin/'));

  protected readonly isAuthenticated = toSignal(this.authService.isAuthenticated$(), { initialValue: false });

  ngOnInit(): void {
    this.buildMenu();

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildMenu();
    });
  }

  private buildMenu() {
    this.items$ = this.translateService.onLangChange.asObservable().pipe(
      startWith(this.translateService.currentLang),
      map(() => this.getDesktopItems()),
    );
  }

  private getDesktopItems(): MenuItem[] {
    // Accessible only to public users (and admin so it's easier to dev)
    const publicAccess =
      !!this.authService.getAuthToken() &&
      (!this.authService.hasAccess('client') || this.authService.hasAccess('admin'));

    // Accessible to client users with at least one of the needed features
    const clientAccess =
      !!this.authService.getAuthToken() &&
      this.authService.hasAccess('client') &&
      (this.authService.hasFeature('station') ||
        this.authService.hasFeature('customerAccount') ||
        this.authService.hasScope('onboarding'));

    return [
      {
        label: this.translateService.instant('menu.map'),
        visible: !!this.authService.getAuthToken(),
        routerLink: 'map',
        icon: 'fa fa-map',
      },
      {
        label: this.translateService.instant('menu.notifications'),
        visible: !!this.authService.getAuthToken(),
        routerLink: 'notifications',
        icon: 'fa fa-bell',
      },
      {
        label: this.translateService.instant('my.menu.stations'),
        visible: publicAccess,
        routerLink: ['my/stations'],
        icon: 'fa fa-building',
      },
      {
        label: this.translateService.instant('admin.menu.management'),
        visible: clientAccess,
        icon: 'fa fa-cog',
        styleClass: this.isGestionActive() ? 'menu-active' : '',

        items: [
          {
            label: this.translateService.instant('admin.menu.stations'),
            visible: this.authService.hasFeature('station'),
            routerLink: ['admin/stations'],
            icon: 'fa fa-building',
          },
          {
            label: this.translateService.instant('admin.menu.customerAccounts'),
            visible: this.authService.hasFeature('customerAccount') || this.authService.hasScope('onboarding'),
            routerLink: ['admin/customer-accounts'],
            icon: 'fa-address-card fas',
          },
        ],
      },
    ];
  }
}
